import { useEffect } from 'react';

/**
 * Close a popover menu on a press outside `ref`, or on Escape, while `open`.
 *
 * Listens on the document instead of laying a full-screen click-catcher
 * under the menu. A catcher covered every other control while the menu was
 * open (on a phone, the sheet's own Close among them), and a tap meant for
 * one of them only shut the menu.
 *
 * Escape is taken in the capture phase and stopped there: a menu inside a
 * sheet closes alone, rather than taking the sheet (whose Escape listener is
 * on the document too) down with it.
 */
export function useDismiss(
  ref: React.RefObject<HTMLElement>,
  open: boolean,
  close: () => void
) {
  useEffect(() => {
    if (!open) return;
    const onPress = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      close();
    };
    document.addEventListener('mousedown', onPress);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onPress);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [ref, open, close]);
}
